/**
 * URL publique d'une colocation.
 *
 * En production, chaque coloc vit sur son sous-domaine `{slug}.coloc.app`.
 * En local ce routage n'existe pas : le middleware de coloc-front ne réécrit
 * que pour les hôtes `*.coloc.app` et renvoie `null` pour tout le reste (voir
 * `extractSlugFromHost`). Seule la route en chemin répond donc sur le serveur
 * de développement.
 *
 * `REACT_APP_PUBLIC_SITE_URL` bascule entre les deux : renseignée en local,
 * absente en production. Comme tout `REACT_APP_*`, elle est figée à la
 * compilation.
 */
const BASE_LOCALE = process.env.REACT_APP_PUBLIC_SITE_URL;

/**
 * `slug` est optionnel dans le type `Coloc`. L'interpolation directe qui
 * précédait ce module produisait alors `https://undefined.coloc.app` — une
 * URL d'apparence valide menant nulle part. On renvoie la racine du site,
 * qui existe, plutôt qu'un sous-domaine fabriqué.
 */
export const urlSitePublic = (slug: string | undefined): string => {
  const racine = BASE_LOCALE || 'https://coloc.app';
  if (!slug) return racine;
  return BASE_LOCALE ? `${BASE_LOCALE}/coloc-details/${slug}` : `https://${slug}.coloc.app`;
};

/**
 * Page publique d'une chambre, sur le site de sa colocation. Même bascule que
 * `urlSitePublic` : le chemin est celui que sert coloc-front partout.
 */
export const urlChambrePublique = (colocSlug: string, roomSlug: string): string =>
  `${BASE_LOCALE || `https://${colocSlug}.coloc.app`}/room-details/${colocSlug}/${roomSlug}`;

/** Même URL, sans le schéma — pour l'afficher sans le bruit du `https://`. */
export const libelleSitePublic = (slug: string | undefined): string =>
  urlSitePublic(slug).replace(/^https?:\/\//, '');
